import { memo } from 'react';
import type { ConfigPlanner, Formato, MedidasPagina, Paleta, Pagina, Secao } from '@miolos/core';
import { ALTURA_CABECALHO_PAGINA_MM, ALTURA_RODAPE_MM, medirPagina, tamanhoFontePt } from '@miolos/core';
import { BlocoImpresso, type ContextoBloco } from './BlocoImpresso';
import { CaixasLivresImpressas } from './CaixasLivres';
import { CamadaImagem } from './CamadaImagem';
import { GuiaDeFuro } from './GuiaDeFuro';
import { MarcasDeCorte } from './MarcasDeCorte';
import { MarcaDagua } from './MarcaDagua';
import { corDoPapel, fontesDe } from './cores';
import { Ornamento } from './Ornamento';
import { PapelDecorado } from './PapelDecorado';
import { Moldura } from './Moldura';

interface Props {
  pagina: Pagina;
  paleta: Paleta;
  config: ConfigPlanner;
  /** Página ímpar (recto) tem a lombada à esquerda. */
  lado: 'ESQUERDO' | 'DIREITO';
  /** Resolve a URL da imagem de fundo (uploads da API). */
  resolverImagem?: (url: string) => string;
  /** Texto da marca d'água (demonstração). Nulo = página limpa. */
  marcaDagua?: string | null;
  /** Linha menor da marca d'água, com o e-mail de quem pediu a demonstração. */
  marcaDaguaDetalhe?: string | null;
  className?: string;
}

/**
 * A medida da folha como a página a calcula: orientação, lombada, margem e
 * sangria saem da configuração, e o lado da lombada vem de quem monta a página.
 *
 * Quem desenha **por cima** da página (as alças do arrasto, no assistente) tem de
 * usar esta mesma função: régua diferente ali e a caixa dançaria em relação ao
 * desenho.
 */
export function medidasDaPagina(
  config: ConfigPlanner,
  formato: Formato,
  lado: 'ESQUERDO' | 'DIREITO',
): MedidasPagina {
  return medirPagina(formato, {
    orientacao: config.papel?.orientacao,
    ladoLombada: config.papel?.ladoLombada,
    margemLombadaMm: config.papel?.margemLombadaMm,
    lado,
    sangriaMm: config.sangriaMm ?? 0,
  });
}

/*
 * `memo` porque a folha é uma árvore grande: no assistente, arrastar uma caixa
 * redesenha o contorno a cada movimento, e nada na página muda até o botão ser
 * solto. Sem isto, a página inteira seria reconciliada a cada quadro do arrasto.
 */
export const PaginaImpressa = memo(function PaginaImpressa({
  pagina,
  paleta,
  config,
  lado,
  resolverImagem,
  marcaDagua,
  marcaDaguaDetalhe,
  className,
}: Props) {
  const medidas = medidasDaPagina(config, pagina.formato, lado);
  const fontes = fontesDe(config.tipografia);
  const ctx: ContextoBloco = {
    paleta,
    formato: pagina.formato,
    fontes,
    escala: config.tipografia.escala,
    trackingTitulos: config.tipografia.trackingTitulos,
    resolverImagem,
    escrita: config.escrita,
    acabamentoDaCaixa: config.acabamentoDaCaixa,
  };

  const fundo =
    pagina.fundo.tipo === 'GRADIENTE'
      ? `linear-gradient(${pagina.fundo.angulo ?? 150}deg, ${pagina.fundo.corA ?? paleta.principal} 0%, ${
          pagina.fundo.corB ?? paleta.destaque
        } 100%)`
      : paleta.fundo;

  const corTexto = pagina.fundo.temaEscuro ? paleta.inverso : paleta.texto;

  const estiloCabecalho = {
    fontSize: `${tamanhoFontePt(pagina.formato, 'XS', config.tipografia.escala)}pt`,
    color: pagina.fundo.temaEscuro ? paleta.inverso : paleta.textoSuave,
  } as const;

  return (
    <section
      className={['pagina-miolo', className].filter(Boolean).join(' ')}
      style={{
        width: `${medidas.larguraMm}mm`,
        height: `${medidas.alturaMm}mm`,
        background: fundo,
        color: corTexto,
        fontFamily: fontes.corpo,
      }}
      data-tipo={pagina.tipo}
    >
      {/*
        Papel decorado: pontilhado, quadriculado, ondinhas ou confete, repetidos
        pela folha inteira. Vem **antes** das camadas de imagem de propósito —
        quem enviou a própria arte de fundo continua vendo a arte dela.
      */}
      <PapelDecorado tipo={config.ornamento} paleta={paleta} />

      {/* Camadas de imagem: fundos, faixas de borda, cantos e figuras soltas */}
      {pagina.camadas?.map((camada, indice) => (
        <CamadaImagem
          key={`${camada.id}-${indice}`}
          camada={camada}
          medidas={medidas}
          verso={lado === 'DIREITO'}
          temaEscuro={pagina.fundo.temaEscuro}
          paleta={paleta}
          resolverImagem={resolverImagem}
        />
      ))}

      <Ornamento tipo={config.ornamento} paleta={paleta} formato={pagina.formato} lado={lado} papel={config.papel} />

      {/* Marcas de corte: só existem quando a folha tem sangria para gráfica. */}
      <MarcasDeCorte medidas={medidas} />

      {/*
        Moldura em volta do conteúdo: nas páginas em que a folha é quase toda
        espaço de escrita (dia, divisória de mês e cadernos). Na capa e nas
        páginas cheias de conteúdo ela competiria com o desenho.
      */}
      {pagina.tipo === 'DIA' || pagina.tipo === 'MES' || pagina.tipo === 'CADERNO' || pagina.tipo === 'DIVISORIA' ? (
        <Moldura
          tipo={config.moldura}
          paleta={paleta}
          formato={pagina.formato}
          lado={lado}
          papel={config.papel}
          corEscolhida={config.molduraCor}
          espessuraMm={config.molduraEspessuraMm}
        />
      ) : null}

      {/* Guia de furo da encadernação: sai impressa quando o cliente pede. */}
      <GuiaDeFuro medidas={medidas} papel={config.papel} lado={lado} />

      {/* Conteúdo, respeitando margens de encadernação */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1000,
          paddingTop: `${medidas.conteudoYMm}mm`,
          paddingBottom: `${medidas.alturaMm - medidas.conteudoYMm - medidas.alturaConteudoMm}mm`,
          paddingLeft: `${medidas.conteudoXMm}mm`,
          paddingRight: `${medidas.larguraMm - medidas.conteudoXMm - medidas.larguraConteudoMm}mm`,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {pagina.cabecalho ? (
          <div
            style={{
              flex: `0 0 ${ALTURA_CABECALHO_PAGINA_MM}mm`,
              height: `${ALTURA_CABECALHO_PAGINA_MM}mm`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '3mm',
              borderBottom: pagina.cabecalho.mostrarRegua ? `0.2mm solid ${paleta.linha}` : undefined,
              marginBottom: '2.5mm',
            }}
          >
            <span style={{ ...estiloCabecalho, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600 }}>
              {pagina.cabecalho.esquerda}
            </span>
            {pagina.cabecalho.centro ? (
              <span style={{ ...estiloCabecalho, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                {pagina.cabecalho.centro}
              </span>
            ) : null}
            <span style={{ ...estiloCabecalho, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              {pagina.cabecalho.direita}
            </span>
          </div>
        ) : null}

        {pagina.secoes.map((secao) => (
          <SecaoImpressa key={secao.id} secao={secao} ctx={ctx} />
        ))}

        {pagina.rodape ? (
          <div
            style={{
              flex: `0 0 ${ALTURA_RODAPE_MM}mm`,
              height: `${ALTURA_RODAPE_MM}mm`,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: '3mm',
              paddingTop: '1.5mm',
              borderTop: pagina.rodape.mostrarRegua ? `0.2mm solid ${paleta.linha}` : undefined,
            }}
          >
            <span style={{ ...estiloCabecalho, opacity: 0.8 }}>{pagina.rodape.esquerda}</span>
            <span style={{ ...estiloCabecalho, fontWeight: 700, color: corTexto }}>
              {pagina.rodape.centro}
            </span>
            <span style={{ ...estiloCabecalho, opacity: 0.8 }}>{pagina.rodape.direita}</span>
          </div>
        ) : null}

        {/*
          Caixas posicionadas à mão: entram como último filho da área de
          conteúdo, mas fora do fluxo — o motor já as tirou das seções, então
          aqui elas só são desenhadas na posição escolhida, por cima do resto.
        */}
        <CaixasLivresImpressas pagina={pagina} medidas={medidas} ctx={ctx} />
      </div>

      {/* Demonstração: marca de cópia não autorizada em cima de tudo */}
      <MarcaDagua texto={marcaDagua} detalhe={marcaDaguaDetalhe} />
    </section>
  );
});

function SecaoImpressa({ secao, ctx }: { secao: Secao; ctx: ContextoBloco }) {
  const { paleta } = ctx;

  const fundo =
    secao.fundo === 'NENHUM'
      ? 'transparent'
      : secao.fundo === 'SUAVE'
        ? paleta.fundoAlt
        : secao.fundo === 'PRINCIPAL'
          ? paleta.principal
          : secao.fundo === 'DESTAQUE'
            ? paleta.destaque
            : secao.fundo === 'DESTAQUE_SUAVE'
              ? paleta.destaqueSuave
              : paleta.principalEscuro;

  const estiloBorda = (() => {
    switch (secao.borda) {
      case 'SUAVE':
        return `0.25mm solid ${paleta.borda}`;
      case 'PRINCIPAL':
        return `0.4mm solid ${paleta.principal}`;
      case 'DESTAQUE':
        return `0.4mm solid ${paleta.destaque}`;
      case 'TRACEJADA':
        return `0.25mm dashed ${paleta.linha}`;
      case 'INVERSO':
        return `0.4mm solid ${paleta.inverso}88`;
      default:
        return undefined;
    }
  })();

  const justificar =
    secao.alinhamentoVertical === 'CENTRO'
      ? 'center'
      : secao.alinhamentoVertical === 'BASE'
        ? 'flex-end'
        : 'flex-start';

  const conteudo =
    secao.blocos && secao.blocos.length > 0 ? (
      secao.blocos.map((bloco, i) => <BlocoImpresso key={i} bloco={bloco} ctx={ctx} />)
    ) : secao.colunas ? (
      <div
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: secao.colunas.map((coluna) => `minmax(0, ${coluna.fracao}fr)`).join(' '),
          gap: `${secao.gapMm}mm`,
        }}
      >
        {secao.colunas.map((coluna, i) => (
          <div
            key={i}
            style={{
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '2.2mm',
            }}
          >
            {coluna.blocos.map((bloco, j) => (
              <BlocoImpresso key={j} bloco={bloco} ctx={ctx} />
            ))}
          </div>
        ))}
      </div>
    ) : null;

  return (
    <div
      style={{
        /*
         * Três jeitos de uma seção ocupar a página:
         *  - com altura declarada: aquela altura, exata;
         *  - com peso 0 e sem altura: ela abraça o próprio conteúdo (é o caso dos
         *    títulos e cabeçalhos de caderno);
         *  - com peso maior que zero: divide o que sobra com as outras.
         *
         * O peso 0 sem altura era tratado como `0 0 0%`, ou seja, altura zero: a
         * seção encolhia e o conteúdo desaparecia, sem aviso.
         */
        flex:
          secao.alturaMm !== null
            ? `0 0 ${secao.alturaMm}mm`
            : secao.peso === 0
              ? '0 0 auto'
              : `${secao.peso} ${secao.peso} 0%`,
        height: secao.alturaMm !== null ? `${secao.alturaMm}mm` : undefined,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: justificar,
        gap: `${secao.gapMm}mm`,
        padding: `${secao.preenchimentoMm}mm`,
        background: fundo,
        border: estiloBorda,
        borderRadius: `${secao.raioMm}mm`,
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {conteudo}
    </div>
  );
}

/** Cor de texto já resolvida, exportada para reuso em telas de revisão. */
export { corDoPapel };
